import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTrip, useCloseTrip, useReopenTrip } from "@/hooks/useTrip";
import { useParticipants } from "@/hooks/useParticipants";
import { useReceipts } from "@/hooks/useReceipts";
import { useSettlements, useMarkDebtPaid, useUnmarkDebtPaid } from "@/hooks/useBalances";
import { PageShell } from "@/components/PageShell";
import { TripHeader } from "@/components/TripHeader";
import { useShareTrip } from "@/hooks/useShareTrip";
import { Skeleton } from "@/components/Skeleton";
import { ProvisionalBanner } from "@/components/ProvisionalBanner";
import { ClosedBanner } from "@/components/ClosedBanner";
import { Button } from "@/components/Button";
import { TransferCard } from "@/components/TransferCard";
import { PaidTransferRow } from "@/components/PaidTransferRow";
import { EmptyState } from "@/components/EmptyState";
import { IncompleteReceiptsNotice } from "@/components/IncompleteReceiptsNotice";
import { CloseReadinessModal } from "@/components/CloseReadinessModal";
import { ConflictError } from "@/api/client";
import { useToast } from "@/components/toast/ToastProvider";
import { deriveReadiness, needsAttention } from "@/utils/receiptReadiness";
import { formatDate, pluralTransfers } from "@/utils/format";
import "./settle-up.css";

/**
 * Single settlement surface (ADR-0025, Option C): the minimal transfer plan.
 * No mode toggle, no pairwise "Хто кому винен" view. Active transfers are the
 * to-do list; fully-paid (incl. rerouted) transfers stay as "✓ оплачено" rows.
 *
 * ADR-0029 additions: a blue <IncompleteReceiptsNotice> below the trip-state
 * banner when ≥1 receipt is not yet reflected in the plan, and a HARD close
 * gate (resolve-or-cancel) — close is blocked, never warned, when any receipt
 * needs attention. The readiness signal is derived in the SPA from the
 * receipts list (no server field).
 */
export function SettleUpScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const tripQ = useTrip(slug);
  const handleShare = useShareTrip(tripQ.data);
  const participantsQ = useParticipants(slug);
  const receiptsQ = useReceipts(slug);
  const settleQ = useSettlements(slug);
  const close = useCloseTrip(slug);
  const reopen = useReopenTrip(slug);
  const markPaid = useMarkDebtPaid(slug);
  const unmark = useUnmarkDebtPaid(slug);

  const [gateOpen, setGateOpen] = useState(false);

  const receipts = useMemo(
    () => receiptsQ.data?.receipts ?? [],
    [receiptsQ.data],
  );
  const readiness = useMemo(() => deriveReadiness(receipts), [receipts]);
  const blockingReceipts = useMemo(
    () => receipts.filter(needsAttention),
    [receipts],
  );

  function goToOffenders(): void {
    navigate(`/t/${slug}/receipts?filter=attention`);
  }

  // HARD GATE (ADR-0029): when any receipt needs attention, the close button
  // does not fire a normal close — it opens the blocking readiness modal so the
  // doomed request never leaves the client. A racing 409 trip_not_ready (stale
  // client / concurrent close) is caught below and surfaced specifically.
  function handleCloseClick(version: number): void {
    if (readiness.counts.total > 0) {
      setGateOpen(true);
      return;
    }
    close.mutate(
      { version },
      {
        onError: (err) => {
          if (
            err instanceof ConflictError &&
            err.conflictReason === "trip_not_ready"
          ) {
            // Stale/concurrent race: the server saw blocking receipts we didn't.
            // Surface a SPECIFIC message (not the generic FX/network error),
            // refetch the list so the notice/gate re-derive, and route to the
            // offenders. useReceipts invalidation happens via the refetch here.
            toast.push({
              tone: "error",
              title: "Спершу заверши чеки, які ще не враховані.",
            });
            void receiptsQ.refetch();
            void tripQ.refetch();
            goToOffenders();
          }
          // optimistic_version_mismatch is handled by useCloseTrip's onError
          // (useConflictToast); other errors fall through to its handling.
        },
      },
    );
  }

  if (tripQ.isLoading || !tripQ.data) {
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={120} />
      </PageShell>
    );
  }

  const trip = tripQ.data;
  const plan = settleQ.data;
  const participants = participantsQ.data?.participants ?? [];
  const isOpen = trip.status === "open";
  const isProvisional = plan?.is_provisional ?? isOpen;

  const transfers = plan?.transfers ?? [];
  const outstanding = transfers.filter((t) => t.settled_state !== "settled");
  const settled = transfers.filter((t) => t.settled_state === "settled");

  const closeAction = (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => handleCloseClick(trip.version)}
      loading={close.isPending}
    >
      Закрити поїздку
    </Button>
  );

  const reopenAction = (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => reopen.mutate({ version: trip.version })}
      loading={reopen.isPending}
    >
      Відкрити поїздку
    </Button>
  );

  const closedAt = trip.closed_at ?? trip.updated_at;
  const closedTitle = closedAt ? `Поїздку закрито ${formatDate(closedAt)}` : "Поїздку закрито";

  const summary =
    settled.length > 0
      ? `Лишилось ${pluralTransfers(outstanding.length)} · сплачено ${settled.length}`
      : pluralTransfers(outstanding.length);

  // Order (components.md §4.2a): banner → notice → summary → plan. The notice
  // renders only when count > 0; on a Closed trip it shows read-only with the
  // reopen-path link (AC-16.11).
  const notice =
    readiness.counts.total > 0 ? (
      <IncompleteReceiptsNotice
        counts={readiness.counts}
        transientOnly={readiness.transientOnly}
        tripClosed={!isOpen}
        onResolve={() =>
          isOpen ? goToOffenders() : navigate(`/t/${slug}/settings`)
        }
      />
    ) : null;

  return (
    <PageShell header={<TripHeader trip={trip} onShare={handleShare} />}>
      <div className="wp-settle">
        {isProvisional ? (
          <ProvisionalBanner
            title="План орієнтовний."
            description="Закрий поїздку, щоб зафіксувати курси та переказати."
            action={closeAction}
          />
        ) : (
          <ClosedBanner title={closedTitle} description="Курси зафіксовано" action={reopenAction} />
        )}

        {notice}

        {settleQ.isLoading || !plan ? (
          <Skeleton variant="block" width="100%" height={120} />
        ) : outstanding.length === 0 ? (
          // Converged ("Усі квити!") once nothing is outstanding — even if paid
          // rows exist, the celebration is the trace (AC-18.2c). Open trip with
          // nothing to split shows the "нічого ділити" empty state instead.
          !isOpen ? (
            <EmptyState
              icon="🎉"
              title="Усі квити!"
              description="У цій поїздці ніхто нікому нічого не винен."
            />
          ) : (
            <EmptyState
              icon="⚖"
              title="Поки що нічого ділити"
              description="Додай чеки — і тут з'являться перші перекази."
            />
          )
        ) : (
          <>
            <h2 className="wp-pscreen__title">План переказів</h2>
            <p className="wp-settle__caption">{summary}</p>

            <div data-testid="transfers-list">
              {outstanding.map((t) => (
                <TransferCard
                  key={`${t.from_participant_id}->${t.to_participant_id}`}
                  transfer={t}
                  ccy={plan.settlement_ccy}
                  provisional={plan.is_provisional}
                  participants={participants}
                  onMarkPaid={() =>
                    markPaid.mutate({
                      fromParticipantId: t.from_participant_id,
                      toParticipantId: t.to_participant_id,
                      body: { amount: t.amount },
                    })
                  }
                  markPaidDisabled={isOpen}
                  gatedHint={isOpen ? "Активне коли закриєш поїздку" : undefined}
                />
              ))}
            </div>

            {settled.length > 0 ? (
              <>
                <div className="wp-settle__divider" role="separator">
                  Сплачено
                </div>
                <div data-testid="paid-list">
                  {settled.map((t) => (
                    <PaidTransferRow
                      key={`paid:${t.from_participant_id}->${t.to_participant_id}`}
                      transfer={t}
                      ccy={plan.settlement_ccy}
                      participants={participants}
                      onUndo={() =>
                        unmark.mutate({
                          fromParticipantId: t.from_participant_id,
                          toParticipantId: t.to_participant_id,
                        })
                      }
                      undoDisabled={unmark.isPending}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>

      <CloseReadinessModal
        open={gateOpen}
        tripSlug={slug}
        blocking={blockingReceipts}
        transientOnly={readiness.transientOnly}
        onResolve={() => {
          setGateOpen(false);
          goToOffenders();
        }}
        onCancel={() => setGateOpen(false)}
      />
    </PageShell>
  );
}
